import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, CheckCircle2, QrCode, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  useOnboardInvitation,
  useWaStart,
  useWaQr,
  useWaStatus,
  useCompleteOnboard,
} from '@/hooks/useOnboard';
import type { OnboardCreds } from '@/api/onboard.api';

export const Route = createFileRoute('/onboard/$token')({
  component: OnboardPage,
});

// Login que el cliente elige para SU panel (se crea el operador al provisionar).
const operatorFields = {
  opUser: z.string().trim().min(3, 'El usuario debe tener al menos 3 caracteres'),
  opPass: z.string().min(8, 'La clave debe tener al menos 8 caracteres'),
  siteUrl: z.string().trim().url('Poné el link completo, ej: https://tucasino.com'),
};
const ganamosSchema = z.object({
  username: z.string().min(1, 'Ingresá el usuario del agente'),
  password: z.string().min(1, 'Ingresá la contraseña'),
  ...operatorFields,
});
const casinozeusSchema = z.object({
  alias: z.string().min(1, 'Ingresá el alias'),
  password: z.string().min(1, 'Ingresá la contraseña'),
  company: z.string().min(1, 'Ingresá la empresa'),
  db: z.coerce.number().int().optional(),
  ...operatorFields,
});

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4 py-8">
      <Card className="w-full max-w-md">{children}</Card>
    </div>
  );
}

function OnboardPage() {
  const { token } = Route.useParams();
  const invitation = useOnboardInvitation(token);
  const [step, setStep] = useState<'creds' | 'wa' | 'done'>('creds');
  const [creds, setCreds] = useState<OnboardCreds | null>(null);
  const [operator, setOperator] = useState<{ username: string; password: string } | null>(null);
  const [siteUrl, setSiteUrl] = useState('');

  if (invitation.isLoading) {
    return (
      <Shell>
        <CardContent className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Shell>
    );
  }

  if (invitation.isError || !invitation.data) {
    return (
      <Shell>
        <CardHeader className="items-center text-center">
          <AlertTriangle className="h-10 w-10 text-destructive" />
          <CardTitle>Invitación no válida</CardTitle>
          <CardDescription>
            Este link venció o ya fue usado. Pedile uno nuevo a tu contacto de Lucky Soft.
          </CardDescription>
        </CardHeader>
      </Shell>
    );
  }

  const inv = invitation.data;

  if (step === 'done') {
    return (
      <Shell>
        <DoneStep displayName={inv.displayName} username={operator?.username ?? ''} />
      </Shell>
    );
  }

  if (step === 'wa') {
    return (
      <Shell>
        <WaStep token={token} creds={creds!} operator={operator!} siteUrl={siteUrl} onConnected={() => setStep('done')} />
      </Shell>
    );
  }

  return (
    <Shell>
      <CredsStep
        platform={inv.platform}
        displayName={inv.displayName}
        onSubmit={(c, op, site) => {
          setCreds(c);
          setOperator(op);
          setSiteUrl(site);
          setStep('wa');
        }}
      />
    </Shell>
  );
}

/** Alta terminada: avisa y lleva al login (el cliente entra con SU usuario y clave). */
function DoneStep({ displayName, username }: { displayName: string; username: string }) {
  const navigate = useNavigate();

  // Lo mandamos al login solo, así no tiene que buscar dónde entrar.
  useEffect(() => {
    const t = setTimeout(() => navigate({ to: '/login' }), 4000);
    return () => clearTimeout(t);
  }, [navigate]);

  return (
    <>
      <CardHeader className="items-center text-center">
        <CheckCircle2 className="h-12 w-12 text-green-600" />
        <CardTitle>¡Listo, {displayName}!</CardTitle>
        <CardDescription>
          Tu WhatsApp quedó conectado y ya podés recibir cargas automáticas.
          {username ? (
            <>
              {' '}
              Entrá a tu panel con el usuario <strong>{username}</strong> y la clave que elegiste.
            </>
          ) : null}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-3">
        <Button className="w-full" onClick={() => navigate({ to: '/login' })}>
          Entrar a mi panel
        </Button>
        <p className="text-xs text-muted-foreground">Te llevamos al inicio de sesión…</p>
      </CardContent>
    </>
  );
}

function CredsStep({
  platform,
  displayName,
  onSubmit,
}: {
  platform: 'ganamos' | 'casinozeus' | 'kingbox' | 'soyjoker';
  displayName: string;
  onSubmit: (creds: OnboardCreds, operator: { username: string; password: string }, siteUrl: string) => void;
}) {
  const usaGanamosForm = platform === 'ganamos' || platform === 'kingbox' || platform === 'soyjoker';
  const schema = usaGanamosForm ? ganamosSchema : casinozeusSchema;
  const form = useForm<any>({
    resolver: zodResolver(schema),
    defaultValues: usaGanamosForm
      ? { username: '', password: '', opUser: '', opPass: '', siteUrl: '' }
      : { alias: '', password: '', company: '', db: 4, opUser: '', opPass: '', siteUrl: '' },
  });

  return (
    <>
      <CardHeader>
        <CardTitle>Hola, {displayName}</CardTitle>
        <CardDescription>
          Cargá las credenciales de tu cuenta de agente ({platform}). Las usamos para acreditar las
          cargas automáticamente.
        </CardDescription>
      </CardHeader>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((v) => {
            // Separar el login del panel (opUser/opPass) y el link de juego (siteUrl) de las creds.
            const { opUser, opPass, siteUrl, ...rest } = v as Record<string, unknown>;
            onSubmit(rest as OnboardCreds, { username: String(opUser).trim(), password: String(opPass) }, String(siteUrl).trim());
          })}
        >
          <CardContent className="space-y-4">
            {usaGanamosForm ? (
              <>
                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Usuario del agente</FormLabel>
                      <FormControl>
                        <Input autoFocus placeholder="miagente" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contraseña</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="••••••••" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            ) : (
              <>
                <FormField
                  control={form.control}
                  name="alias"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Alias</FormLabel>
                      <FormControl>
                        <Input autoFocus placeholder="mialias" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contraseña</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="••••••••" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Empresa</FormLabel>
                      <FormControl>
                        <Input placeholder="CZEC" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <div className="border-t pt-4">
              <p className="mb-3 text-sm font-medium">Acceso a tu panel</p>
              <p className="mb-3 text-xs text-muted-foreground">
                Elegí el usuario y la clave con los que vas a entrar a administrar tus cargas.
              </p>
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="opUser"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Usuario del panel</FormLabel>
                      <FormControl>
                        <Input placeholder="micajero" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="opPass"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Clave del panel</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="mínimo 8 caracteres" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <div className="border-t pt-4">
              <p className="mb-3 text-sm font-medium">Tu casino</p>
              <p className="mb-3 text-xs text-muted-foreground">
                El link de tu plataforma. El bot se lo pasa al jugador para que entre a jugar.
              </p>
              <FormField
                control={form.control}
                name="siteUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Link de la plataforma</FormLabel>
                    <FormControl>
                      <Input placeholder="https://tucasino.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
          <CardContent>
            <Button type="submit" className="w-full">
              Conectar WhatsApp
            </Button>
          </CardContent>
        </form>
      </Form>
    </>
  );
}

function WaStep({
  token,
  creds,
  operator,
  siteUrl,
  onConnected,
}: {
  token: string;
  creds: OnboardCreds;
  operator: { username: string; password: string };
  siteUrl: string;
  onConnected: () => void;
}) {
  const start = useWaStart(token);
  const [instance, setInstance] = useState<string | null>(null);
  const qr = useWaQr(token, !!instance);
  const status = useWaStatus(token, !!instance);
  const complete = useCompleteOnboard(token);
  const [finishing, setFinishing] = useState(false);

  // Arranca la instance una sola vez.
  useEffect(() => {
    if (!start.data && !start.isPending) {
      start.mutate(undefined, { onSuccess: (r) => setInstance(r.instance) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cuando el gateway reporta 'open', provisiona y termina.
  useEffect(() => {
    if (status.data?.status === 'open' && instance && !finishing && !complete.isSuccess) {
      setFinishing(true);
      complete.mutate(
        { creds, waInstance: instance, operator, siteUrl },
        { onSuccess: onConnected, onError: () => setFinishing(false) },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.data?.status, instance]);

  return (
    <>
      <CardHeader className="items-center text-center">
        <QrCode className="h-8 w-8 text-primary" />
        <CardTitle>Conectá tu WhatsApp</CardTitle>
        <CardDescription>
          Abrí WhatsApp → Dispositivos vinculados → Vincular dispositivo, y escaneá el código.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-4">
        {finishing ? (
          <div className="flex flex-col items-center gap-2 py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Conectando tu cuenta…</p>
          </div>
        ) : qr.data?.qr ? (
          <img
            src={qr.data.qr}
            alt="QR de WhatsApp"
            className="h-64 w-64 rounded-lg border bg-white p-2"
          />
        ) : (
          <div className="flex h-64 w-64 items-center justify-center rounded-lg border">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          {status.data?.status === 'open'
            ? 'Conectado ✓'
            : instance
              ? 'Esperando a que escanees el código…'
              : 'Generando código…'}
        </p>
      </CardContent>
    </>
  );
}
